import Checkbox from '@mui/material/Checkbox';
import clsx from "clsx";
import { memo, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import ChipsBox, { CategoryItems } from "../../../../app/components/chipsBox/ChipsBox";
import DateSelect from '../../../../app/components/filter-products/DateSelect';
import InputFilterNoOptions from '../../../../app/components/filter-products/InputFilterNoOptions';
import SelectDropdown from "../../../../app/components/filter-products/SelectDropdown";
import SelectDropdownKeyValue from '../../../../app/components/filter-products/SelectDropdownKeyValue';
import { COLORS, ROLES } from "../../../../core/consts/redesign/commonRedesign";
import { ArrowDown, ArrowUp, CheckBox, CheckedBox, Settings } from "../../../../core/consts/redesign/icons";
import useIsMobile from "../../../../core/hooks/useIsMobile";
import { OptionType } from '../../../../core/types/common';
import { ClientTypeOption } from '../../../../core/types/managerApiTypes';
import { rootState } from '../../../../core/types/rootState';
import classes from "./UsersFilter.module.scss";

type PropsType = {
  searchQuery?: string,
  setSearchQuery?: (prev: string | ((prev: string) => string)) => void,
  agentsList?: OptionType[] | undefined;
  searchAgent?: string;
  setSearchAgent?: (value: string) => void;
  managersList?: OptionType[] | undefined;
  searchManager?: string;
  setSearchManager?: (value: string) => void;
  clientTypeList?: ClientTypeOption[],
  statusList?: string[] | undefined;
  searchStatus?: string;
  setSearchStatus?: (value: string) => void;
  setSearchClientType: (value: string) => void;
  searchClientType: string;
  registrationDate?: string;
  setRegistrationDate?: (value: string) => void;
  onClear: () => void;
  onClearQuery: () => void;
  isClients?: boolean;
  setIsClients: React.Dispatch<React.SetStateAction<boolean>>;
  isAgents?: boolean;
  setIsAgents: React.Dispatch<React.SetStateAction<boolean>>;
  isClientsAgents?: boolean;
  setIsClientsAgents: React.Dispatch<React.SetStateAction<boolean>>;
  isExistedOnPyrus?: boolean;
  setIsExistedOnPyrus: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  handleFilterClick?: () => void;
  setIsFiltersChanged?: React.Dispatch<React.SetStateAction<boolean>>;
}

const UsersFilter = ({
  searchQuery,
  setSearchQuery,
  agentsList,
  searchAgent,
  setSearchAgent,
  managersList,
  clientTypeList,
  searchManager,
  setSearchManager,
  setSearchClientType,
  searchClientType,
  statusList,
  searchStatus,
  setSearchStatus,
  registrationDate,
  setRegistrationDate,
  onClear,
  onClearQuery,
  isClients,
  setIsClients,
  isAgents,
  setIsAgents,
  isClientsAgents,
  setIsClientsAgents,
  isExistedOnPyrus,
  setIsExistedOnPyrus,
  setCurrentPage,
  handleFilterClick,
  setIsFiltersChanged
}: PropsType) => {
  const initialUser = useSelector((state: rootState) => state.currentUser);
  const isAdmin = initialUser.role === ROLES.ADMIN;
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [showResetAll, setShowResetAll] = useState<boolean>(false);
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [selectedClientType, setSelectedClientType] = useState<string>('');

  useEffect(() => {
    const hasFilters = (
      (searchStatus?.length || 0) > 0 ||
      (searchAgent?.length || 0) > 0 ||
      selectedManager?.length > 0 ||
      selectedAgent?.length > 0 ||
      selectedClientType?.length > 0 ||
      (searchClientType?.length || 0) > 0 ||
      (searchManager?.length || 0) > 0 ||
      (searchClientType?.length || 0) > 0 ||
      (isClients || false) ||
      (isAgents || false) ||
      (isClientsAgents || false) ||
      (isExistedOnPyrus || false) ||
      (registrationDate?.length! > 0)
    );
    setShowResetAll(hasFilters);
  }, [
    searchStatus,
    isClients,
    isAgents,
    isClientsAgents,
    isExistedOnPyrus,
    searchAgent,
    searchClientType,
    searchManager,
    selectedAgent?.length,
    selectedManager?.length,
    selectedClientType?.length,
    registrationDate
  ]);

  const accordionToggle = () => {
    setExpanded(!expanded);
  };

  const handleQueryChange = (query: string) => {
    setCurrentPage(1);
    setSearchQuery && setSearchQuery(query);
  };

  const handleAgentChange = (agent: OptionType) => {
    setCurrentPage(1);
    setSelectedAgent(agent.label)
    setSearchAgent && setSearchAgent(agent.value);
  };

  const handleManagerChange = (manager: OptionType) => {
    setCurrentPage(1);
    setSelectedManager(manager.label);
    if (setSearchManager) {
      setSearchManager(manager.value);
    }
  };

  const handleStatusChange = (status: string) => {
    setCurrentPage(1);
    setSearchStatus && setSearchStatus(status);
  }

  const handleClientTypeChange = (type: OptionType) => {
    setCurrentPage(1);
    setSelectedClientType(type.label)
    setSearchClientType && setSearchClientType(type.value);
  };

  const handleDateChange = (date: string) => {
    setCurrentPage(1);
    setRegistrationDate && setRegistrationDate(date);
  }

  const handleAllClear = () => {
    setSelectedAgent('');
    setSelectedManager('');
    setSelectedClientType('');
    setRegistrationDate && setRegistrationDate('');
    onClear();
  };

  const handleDeleteStatus = () => {
    setCurrentPage(1);
    setSearchStatus && setSearchStatus('');
  };

  const handleDeleteDate = () => {
    setCurrentPage(1);
    setRegistrationDate && setRegistrationDate('');
  };

  const handleDeleteClientType = () => {
    setCurrentPage(1);
    setSelectedClientType('');
    setSearchClientType && setSearchClientType('');
  };

  const handleDeleteAgent = () => {
    setCurrentPage(1);
    setSelectedAgent('');
    setSearchAgent && setSearchAgent('');
  };

  const handleDeleteManager = () => {
    setCurrentPage(1);
    setSelectedManager('')
    setSearchManager && setSearchManager('');
  };

  const handleDeleteOnlyClients = () => {
    setCurrentPage(1);
    setIsClients && setIsClients(false);
  };

  const handleDeleteOnlyAgents = () => {
    setCurrentPage(1);
    setIsAgents && setIsAgents(false);
  };

  const handleDeleteExistedOnPyrus = () => {
    setCurrentPage(1);
    setIsExistedOnPyrus && setIsExistedOnPyrus(false);
  };

  const handleDeleteClientsAgents = () => {
    setCurrentPage(1);
    setIsClientsAgents && setIsClientsAgents(false);
  };

  const categories: CategoryItems[] = [
    {
      label: 'status',
      items: searchStatus || [],
      onDeleteItem: handleDeleteStatus,
    },
    {
      label: 'Agent',
      items: selectedAgent || [],
      onDeleteItem: handleDeleteAgent,
    },
    {
      label: 'Manager',
      items: selectedManager || [],
      onDeleteItem: handleDeleteManager,
    },
    {
      label: 'Client Type',
      items: selectedClientType || [],
      onDeleteItem: handleDeleteClientType,
    },
    {
      label: 'isClients',
      items: isClients!,
      onDeleteBoolean: handleDeleteOnlyClients
    },
    {
      label: 'isAgents',
      items: isAgents!,
      onDeleteBoolean: handleDeleteOnlyAgents
    },
    {
      label: 'isClientsAgents',
      items: isClientsAgents!,
      onDeleteBoolean: handleDeleteClientsAgents
    },
    {
      label: 'isExistedOnPyrus',
      items: isExistedOnPyrus!,
      onDeleteBoolean: handleDeleteExistedOnPyrus
    },
    {
      label: 'registrationDate',
      items: registrationDate || '',
      onDeleteItem: handleDeleteDate
    },
  ];

  return (
    <div className={classes.searchWrap} onClick={handleFilterClick}>
      <div className={classes.form}>
        {isMobile ? (
          <>
            <InputFilterNoOptions
              id='query'
              onClear={onClearQuery}
              onChange={handleQueryChange}
              label={"Поиск по имени, ИНН или телефону клиента"}
              placeholder={"Начните вводить запрос для поиска"}
              initialFilter={searchQuery}
            />
            {!expanded &&
              <div
                onClick={accordionToggle}
                className={clsx(classes.rowFlex, classes.filterHover)}
                style={{ margin: 0 }}>
                <div className={classes.filterFlex}>
                  <Settings color={COLORS.brand_blue} />
                  <span className={classes.filterText}>показать фильтры</span>
                </div>
                <ArrowDown color={COLORS.brand_blue} />
              </div>}

            <div className={!expanded ? classes.accordion : classes.accordionExpanded}>
              <SelectDropdownKeyValue
                id='clientType'
                value={selectedClientType || ''}
                label={"Тип пользователя"}
                onChange={handleClientTypeChange}
                placeholder={"Все типы"}
                options={clientTypeList || []}
                setIsFiltersChanged={setIsFiltersChanged}
              />
              <SelectDropdownKeyValue
                id='agents'
                value={selectedAgent || ''}
                label={"Агент"}
                onChange={handleAgentChange}
                placeholder={"Все агенты"}
                options={agentsList || []}
              />
              {isAdmin &&
                <>
                  <SelectDropdownKeyValue
                    id='managers'
                    value={selectedManager || ''}
                    label={"Менеджер"}
                    onChange={handleManagerChange}
                    placeholder={"Все менеджеры"}
                    options={managersList || []}
                    setIsFiltersChanged={setIsFiltersChanged}
                  />
                </>
              }
              <DateSelect
                id='date'
                value={registrationDate || ''}
                label={"Дата регистрации"}
                onChange={handleDateChange}
                placeholder={"Любая дата"}
                setIsFiltersChanged={setIsFiltersChanged}
              />
              <SelectDropdown
                id='status'
                value={searchStatus || ''}
                label={"Статус"}
                onChange={handleStatusChange}
                placeholder={"Все статусы"}
                options={statusList || []}
                setIsFiltersChanged={setIsFiltersChanged}
              />
              <div className={classes.checkboxes}>
                <div className={classes.columnBox}>
                  <div className={classes.checkboxBox}>
                    <Checkbox
                      color="primary"
                      className={classes.checkbox}
                      checked={isClients}
                      sx={{
                        padding: '0',
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                      checkedIcon={<CheckedBox />}
                      icon={<CheckBox />}
                      onChange={() => setIsClients(!isClients)}
                    />
                    <div className={classes.checkboxText}>
                      Показать только клиентов
                    </div>
                  </div>
                  <div className={classes.checkboxBox}>
                    <Checkbox
                      color="primary"
                      className={classes.checkbox}
                      checked={isClientsAgents}
                      sx={{
                        padding: '0',
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                      checkedIcon={<CheckedBox />}
                      icon={<CheckBox />}
                      onChange={() => setIsClientsAgents(!isClients)}
                    />
                    <div className={classes.checkboxText}>
                      Показать только клиентов агента
                    </div>
                  </div>
                </div>
                <div className={classes.columnBox}>
                  <div className={classes.checkboxBox}>
                    <Checkbox
                      color="primary"
                      className={classes.checkbox}
                      checked={isAgents}
                      sx={{
                        padding: '0',
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                      checkedIcon={<CheckedBox />}
                      icon={<CheckBox />}
                      onChange={() => setIsAgents(!isClients)}
                    />
                    <div className={classes.checkboxText}>
                      Показать только агентов
                    </div>
                  </div>
                  <div className={classes.checkboxBox}>
                    <Checkbox
                      color="primary"
                      className={classes.checkbox}
                      checked={isExistedOnPyrus}
                      sx={{
                        padding: '0',
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                      checkedIcon={<CheckedBox />}
                      icon={<CheckBox />}
                      onChange={() => setIsExistedOnPyrus(!isExistedOnPyrus)}
                    />
                    <div className={classes.checkboxText}>
                      Есть в Pyrus
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {expanded && (
              <div onClick={accordionToggle} className={clsx(classes.rowFlex, classes.filterHover)}>
                <div className={classes.filterFlex}>
                  <Settings color={COLORS.brand_blue} />
                  <span className={classes.filterText}>скрыть фильтры</span>
                </div>
                <ArrowUp color={COLORS.brand_blue} />
              </div>
            )}
          </>
        ) : (
          <>
            <div className={classes.rowFlex}>
              <InputFilterNoOptions
                id='query'
                onClear={onClearQuery}
                onChange={handleQueryChange}
                label={"Поиск по имени, инн или телефону пользователя"}
                placeholder={"Начните вводить запрос для поиска"}
                initialFilter={searchQuery}
              />
              <div className={classes.line_y} />
              <SelectDropdownKeyValue
                id='clientType'
                value={selectedClientType || ''}
                label={"Тип пользователя"}
                onChange={handleClientTypeChange}
                placeholder={"Все типы"}
                options={clientTypeList || []}
                setIsFiltersChanged={setIsFiltersChanged}
              />
            </div>

            <div className={classes.rowFlex}>
              <SelectDropdownKeyValue
                id='agents'
                value={selectedAgent || ''}
                label={"Агент"}
                onChange={handleAgentChange}
                placeholder={"Все агенты"}
                options={agentsList || []}
              />
              <div className={classes.line_y} />
              {isAdmin &&
                <>
                  <SelectDropdownKeyValue
                    id='managers'
                    value={selectedManager || ''}
                    label={"Менеджер"}
                    onChange={handleManagerChange}
                    placeholder={"Все менеджеры"}
                    options={managersList || []}
                    setIsFiltersChanged={setIsFiltersChanged}
                  />
                  <div className={classes.line_y} />
                </>
              }
              <DateSelect
                id='date'
                value={registrationDate || ''}
                label={"Дата регистрации"}
                onChange={handleDateChange}
                placeholder={"Любая дата"}
                setIsFiltersChanged={setIsFiltersChanged}
              />
            </div>

            <div className={classes.rowFlex}>
              <SelectDropdown
                id='status'
                noBorder
                value={searchStatus || ''}
                label={"Статус"}
                onChange={handleStatusChange}
                placeholder={"Все статусы"}
                options={statusList || []}
                setIsFiltersChanged={setIsFiltersChanged}
              />
              <div className={classes.line_y} />
              <div className={classes.checkboxes}>
                <div className={classes.columnBox}>
                  <div className={classes.checkboxBox}>
                    <Checkbox
                      color="primary"
                      className={classes.checkbox}
                      checked={isClients}
                      sx={{
                        padding: '0',
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                      checkedIcon={<CheckedBox />}
                      icon={<CheckBox />}
                      onChange={() => setIsClients(!isClients)}
                    />
                    <div className={classes.checkboxText}>
                      Показать только клиентов
                    </div>
                  </div>
                  <div className={classes.checkboxBox}>
                    <Checkbox
                      color="primary"
                      className={classes.checkbox}
                      checked={isClientsAgents}
                      sx={{
                        padding: '0',
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                      checkedIcon={<CheckedBox />}
                      icon={<CheckBox />}
                      onChange={() => setIsClientsAgents(!isClientsAgents)}
                    />
                    <div className={classes.checkboxText}>
                      Показать только клиентов агента
                    </div>
                  </div>
                </div>
                <div className={classes.columnBox}>
                  <div className={classes.checkboxBox}>
                    <Checkbox
                      color="primary"
                      className={classes.checkbox}
                      checked={isAgents}
                      sx={{
                        padding: '0',
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                      checkedIcon={<CheckedBox />}
                      icon={<CheckBox />}
                      onChange={() => setIsAgents(!isAgents)}
                    />
                    <div className={classes.checkboxText}>
                      Показать только агентов
                    </div>
                  </div>
                  <div className={classes.checkboxBox}>
                    <Checkbox
                      color="primary"
                      className={classes.checkbox}
                      checked={isExistedOnPyrus}
                      sx={{
                        padding: '0',
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                      checkedIcon={<CheckedBox />}
                      icon={<CheckBox />}
                      onChange={() => setIsExistedOnPyrus(!isExistedOnPyrus)}
                    />
                    <div className={classes.checkboxText}>
                      Есть в Pyrus
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>)}
      </div>
      <ChipsBox
        id='filter-clients'
        categories={categories}
        showResetAll={showResetAll}
        onResetAll={handleAllClear}
      />
    </div>
  )
}

export default memo(UsersFilter)
